export type InstantConfigRepository = {
  /**
   * Defines what percentage of traffic should be sent to DW.
   * The key value contains the percentage of traffic (number 0-100).
   *
   * ICBM value is JSON with a structure like:
   * { "name": 50 }
   *
   * Example:
   * {"adengloadtimes": 50}
   * which result creating threshold of 50% of the traffic for endpoint: adengloadtimes
   * https://services.fandom.com/icbm/dashboard/feature_flag/odysseyBeaconSenderTrafficLimiter
   */
  odysseyBeaconSenderTrafficLimiter: {
    [key in DataWarehouseEndpointSuffix]: PercentagePoints;
  };
  /**
   * Defines what percentage of traffic should be sent to Real Time monitoring.
   * The endpoint is the ad-engine-monitoring Pandora service which stores metrics in Prometheus.
   * The key value contains the percentage of traffic (number 0-100).
   *
   * ICBM value is JSON with a structure like:
   * { "name": 50 }
   *
   * Example:
   * {"time": 50}
   * which result creating threshold of 50% of the traffic for endpoint: time
   * https://services.fandom.com/icbm/dashboard/feature_flag/odysseyPrometheusSenderTrafficLimiter
   */
  odysseyPrometheusSenderTrafficLimiter: {
    [key in PrometheusEndpointSuffix]: PercentagePoints;
  };
  /**
   * Defines whether A9 is enabled as a bidder
   */
  icA9Bidder: boolean;
  /**
   * Note: This flag is ignored if icA9CleanHEM is set to true.
   *
   * Enables HEM syncing for A9.
   * This allows the AMZN-Token to be created for Amazon Publisher Audiences.
   * */
  icA9HEM: boolean;
  /**
   * This enables the cleanup of HEM related features,
   * including the AMZN-Token and all the entries related to apstag that
   * are stored in localStorage.
   *
   * When this is enabled, it overrides the icA9HEM flag, meaning that if that flag is set to true,
   * the HEM syncing will be disabled, and only cleanup will occur.
   *
   * This feature is handy when we need to clear all of the users' data related to A9,
   * such as when offboarding would need to happen from A9.
   * */
  icA9CleanHEM: boolean;
  icA9VideoBidder: boolean;
  icAdEngineDelay: number;
  icAnyclipPlayer: boolean;
  /**
   * This enables the Assertive Yield plugin, which is used for price floors.
   */
  icAssertiveYield: boolean;
  /**
   * Assertive Yield floors configuration.
   */
  icAssertiveYieldFloors: {
    enabled?: boolean;
    /**
     * The currency used by the Ad Server (GAM). AssertiveYield default is "USD"
     */
    currency: string;
    /**
     * Enable Prebid Floors. AssertiveYield default is true
     */
    prebid: boolean;
    /**
     * Defines the price buckets for GAM Unified Price Rules (UPRs). AssertiveYield default is {min: 0, max: 3, increment: 0.05}
     */
    priceBuckets?: {
      min: number;
      max: number;
      increment: number;
    };
    /**
     * Add the floor to the hash key for accurate attribution. AssertiveYield default is true
     */
    addToHashKey?: boolean;
    limit: {
      /**
       * Whitelist of slotIds to enabled reduction on.
       * If not provided, it defaults to all slots.
       */
      slotIds: string[];
      /**
       * The percentage of users on whom AssertiveYield floors should run.
       * This is traffic limiting done within Assertive Yield, and not by ICBM.
       * Should never be 100%, because we need certain percentage for Control
       * (a portion of the traffic where we are not setting up floors to keep as a benchmark).
       * Represented as a decimal (e.g., 0.9 for 90%). AssertiveYield default is 0.98
       */
      percentage: number;
    };
  };
  /**
   * Is audigent enabled.
   */
  icAudigent: boolean;
  /**
   * Maximum number of segments allowed to be added to targeting. This ensures that we don't add
   * too many and exceed the character limits on a call to the ad server.
   */
  icAudigentSegmentLimit: number;
  /**
   * Override to ensure that appnexus gets at least the segment required to deliver specific
   * campaigns when au_seg doesn't make it to the page in time for the first bids. This is a single
   * segment that will be added only to the appnexus bid stream in Prebid, and only applied with
   * AU_SEG keyvalue is still "-1" at the time of bidding.
   */
  icForceDefaultAuSegOnAppNexus: string;
  icBabDetection: boolean;
  icBidsTracking: boolean;
  icBrandMetrics: boolean;
  icBTRec: boolean;
  icBTRecSideUnits: boolean;
  icConfiant: boolean;
  icCaptify: boolean;
  icConnatixPlayer: boolean;
  icDeHeartbeatActivityTracking: boolean;
  icDeHeartbeatTracking: number[];
  icDeHeartbeatTrackingDev: number[];
  icDisableIncontentPlayerRemoval: boolean;
  icDisableLegacyTrackingParameters: boolean;
  icDisplayAndVideoAdsSyncEnabled: boolean;
  icDoubleVerify: boolean;
  icDSA: boolean;
  icDSAModal: boolean;
  icExperimentBucket: number;
  icExperiments: string[];
  icFeaturedVideoAdsFrequency: number;
  icFeaturedVideoForceVideoAdsEverywhereExcept2ndVideo: boolean;
  icFeaturedVideoForceVideoAdsEverywhereExceptSponsoredVideo: boolean;
  icFeaturedVideoPlayer: string;
  icFloatingMedrecDestroyable: boolean;
  icFloorAdhesionDelay: number;
  icFloorAdhesionViewportsToStart: number;
  icForceStickyTlb: boolean;
  icHiViLeaderboardSlot: boolean;
  icIASPublisherOptimization: boolean;
  icIdentityPartners: boolean;
  icIncontentLeaderboard: boolean;
  icInterventionTracking: boolean;
  icLABradorGamKeyValues: string[];
  icLiveConnect: boolean;
  icLiveConnectCachingStrategy: {
    ttl: number;
    type: "local" | "session";
    mandatoryParams: string[];
  };
  icLiveConnectQf: number;
  icLiveIntentConnectedId: boolean; // Confirm we do not want to prefix this with icPrebid
  icLotame: boolean;
  icMagniteS2sVideo: boolean;
  icMonitorTrafficThreshold: {
    default: number;
    identity: number;
  };
  icNielsen: boolean;
  icOpenRtb2Context: boolean;
  icOpenWeb: {
    spotId: string;
    isActive: boolean;
  };
  icPauseJWPlayerAd: boolean;
  icPlayerTracking: boolean;
  icPostmessageTracking: boolean;
  icPpid: boolean;
  icPpidRepository: string;
  icPrebid: boolean;
  icPrebidAppNexus: boolean;
  icPrebidAppNexusAst: boolean;
  icPrebidAps: boolean;
  icPrebidAuctionDelay: number;
  icPrebidFledgeGPS: boolean;
  icPrebidForceInArticleVideoPlacement: boolean;
  icPrebidFreewheel: boolean;
  icPrebidGumGum: boolean;
  icPrebidId5: boolean;
  icPrebidId5ExternalModule: boolean;
  icPrebidIX: boolean;
  icPrebidIntentIQ: boolean;
  icPrebidIntentIQTimeout: number;
  icPrebidIntentIQVersion: string;
  icPrebidKargo: boolean;
  icPrebidMagniteS2s: boolean;
  icPrebidMedianet: boolean;
  icPrebidNobid: boolean;
  icPrebidOpenX: boolean;
  icPrebidOzone: boolean;
  icPrebidPubmatic: boolean;
  icPrebidRubicon: boolean;
  icPrebidRubiconDisplay: boolean;
  icPrebidS2sBidders: string[];
  icPrebidSeedtag: boolean;
  icPrebidTriplelift: boolean;
  icPrebidUID2: boolean;
  icPrebidVerizon: boolean;
  icPrebidVersion: string;
  icPrebidYahooConnectId: boolean;
  icPreferredCMP: string;
  icRotatorDelay: Record<string, number>;
  icSlotTracking: boolean;
  icStickyTlbWithFV: boolean;
  icSystem1: boolean;
  icTargetingStrategy: string;
  icTlbSizeReduction: boolean;
  icUAPExtendendSrcTargeting: boolean;
  icUAPJWPlayer: boolean;
  icUAPJWPlayerDelay: number;
  icUAPJWPlayerLineItemIds: string[];
  icViewabilityTracking: boolean;
  stateHideSlotPlaceholder: boolean;
  stateRoadblockV2: boolean;

  // new AE4 variables
  icRefreshTimeoutMS: number;
  icPrebidSharedId: boolean;
  icBlockthroughReadershipLink: boolean; // Enable/Disable BlockthroughReadershipLinkPlugin
  // new timeouts

  /**
   * Timeout for the A9/Prebid auction.
   * This is the maximum time allowed for the auction to complete before the failsafe is triggered.
   */
  icAdEngineAuctionTimeoutMS: number;
  /**
   * Timeout for the `onCreateBiddersRequests` hook to complete before giving up and
   * continuing with the render cycle.
   * This is the maximum time allowed for the bidders to create requests.
   */
  icAdEngineOnCreateBiddersRequestsTimeoutMS: number;
  /**
   * Timeout for the onBiddersInit hook to complete before giving up and
   * continuing with the render cycle.
   * This is the maximum time allowed for the bidders to initialize.
   */
  icAdEngineBidderInitTimeoutMS: number;
  /**
   * Timeout for the Prebid config update.
   * This is the maximum time allowed for the Prebid config update to complete before timing out.
   */
  icPrebidConfigUpdateTimeoutMS: number;
  /**
   * Timeout for the {@link PrebidConsentManagementPlugin}.
   * This is the maximum time allowed for the consent management to complete before timing out.
   */
  icPrebidConsentManagementTimeoutMS: number;
  /**
   * How long to wait for Prebid.js to initialize before giving up and throwing an
   * error that prebid isn't ready.
   * This is the maximum time allowed for Prebid.js to initialize before timing out.
   */
  icPrebidInitTimeoutMS: number;
  icTieredMonetizationEnabled: boolean;
  /**
   * How long to wait before the first ads request is made.
   * This is useful to ensure that all the necessary scripts and configurations are loaded
   * before the first ads request is made.
   * If set to 0, it will not wait and will proceed with the first ads request immediately.
   * If set to a positive number, it will wait for that many milliseconds before making the
   * first ads request.
   */
  icAdEngineWaitBeforeFirstAdsRequestMS: number;
};

export const instantConfigDefaultValues: Partial<InstantConfigRepository> = {
  icPrebidIntentIQTimeout: 500,
  icPrebidIntentIQVersion: "6.06-20240711",
  icPreferredCMP: "onetrust",
  // new timeouts
  icAdEngineWaitBeforeFirstAdsRequestMS: 0,
  icAdEngineAuctionTimeoutMS: 1500,
  icAdEngineOnCreateBiddersRequestsTimeoutMS: 1510,
  /**
   * Added few MS just to ensure any timeouts that are 1000ms get the chance
   * to complete.
   */
  icAdEngineBidderInitTimeoutMS: 1005,
  icPrebidConfigUpdateTimeoutMS: 1000,
  icPrebidConsentManagementTimeoutMS: 1500,
  icPrebidInitTimeoutMS: 500,
};

type PercentagePoints = number;

export type DataWarehouseEndpointSuffix =
  | "adengadsizeinfo"
  | "adengbidders"
  | "adengadinfo"
  | "adenglabradorinfo"
  | "adengloadtimes"
  | "identityinfo"
  | "trackingevent";

export type PrometheusEndpointSuffix = "time";
